import { DEFAULT_SHADER_MODELS, loadShaderProviderDefaults } from '@/lib/canvas/shader/authoring'
import {
  DEFAULT_LOCAL_BASE_URL,
  SHADER_PROVIDER_IDS,
  SHADER_PROVIDER_LABELS,
  type ShaderProviderConfig,
  type ShaderProviderId,
} from '@/lib/canvas/shader/authoring/types'

/**
 * A node's provider choice comes from saved canvas state, which may have been
 * written by someone else (a shared, imported or duplicated canvas). It picks a
 * provider and model; it never picks where the user's prompt is sent. A Local
 * server address is honoured only when this browser's user entered it, or it is
 * their default; anything else falls back to their own address.
 */

const TRUSTED_SERVERS_KEY = 'genie.shader.local.servers'
const MAX_TRUSTED_SERVERS = 20

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isProviderId(value: unknown): value is ShaderProviderId {
  return typeof value === 'string' && (SHADER_PROVIDER_IDS as readonly string[]).includes(value)
}

/** One spelling per server: scheme, host and path, without a trailing slash. */
function serverKey(url: string): string {
  try {
    const parsed = new URL(url.trim())
    return `${parsed.protocol}//${parsed.host}${parsed.pathname.replace(/\/+$/, '')}`
  } catch {
    return url.trim().replace(/\/+$/, '')
  }
}

function loadTrustedServers(): string[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(TRUSTED_SERVERS_KEY) ?? '[]') as unknown
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : []
  } catch {
    return []
  }
}

/** Records a Local server address the user typed themselves in this browser. */
export function trustLocalServer(url: string): void {
  const key = serverKey(url)
  if (!key) return
  const next = [key, ...loadTrustedServers().filter((item) => item !== key)].slice(0, MAX_TRUSTED_SERVERS)
  try {
    window.localStorage.setItem(TRUSTED_SERVERS_KEY, JSON.stringify(next))
  } catch {
    // Storage unavailable: the address falls back to the default on the next read.
  }
}

export function isTrustedLocalServer(url: string, defaults: ShaderProviderConfig): boolean {
  const key = serverKey(url)
  if (key === serverKey(DEFAULT_LOCAL_BASE_URL)) return true
  if (defaults.provider === 'local' && defaults.baseUrl && key === serverKey(defaults.baseUrl)) return true
  return loadTrustedServers().includes(key)
}

/** The config a node generates with: its stored choice, checked, else the user's default. */
export function resolveShaderProviderConfig(
  stored: unknown,
  defaults: ShaderProviderConfig = loadShaderProviderDefaults(),
): ShaderProviderConfig {
  if (!isRecord(stored) || !isProviderId(stored.provider)) return defaults
  const provider = stored.provider
  const model = typeof stored.model === 'string' && stored.model.trim()
    ? stored.model
    : defaults.provider === provider ? defaults.model : DEFAULT_SHADER_MODELS[provider]
  const config: ShaderProviderConfig = { provider, model }
  if (provider === 'openrouter') {
    config.billing = stored.billing === 'own-key' || stored.billing === 'genie-credits' ? stored.billing : 'genie-credits'
  }
  if (provider === 'local') {
    const own = defaults.provider === 'local' ? defaults.baseUrl : undefined
    const requested = typeof stored.baseUrl === 'string' ? stored.baseUrl.trim() : ''
    const baseUrl = requested && isTrustedLocalServer(requested, defaults) ? requested : own
    if (baseUrl) config.baseUrl = baseUrl
  }
  return config
}

function hostOf(url: string): string {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

/** One line naming who writes the shader and, for Local, where the prompt goes. */
export function describeShaderProvider(config: ShaderProviderConfig): string {
  const parts = [SHADER_PROVIDER_LABELS[config.provider], config.model]
  if (config.provider === 'openrouter') parts.push(config.billing === 'own-key' ? 'your key' : 'Genie credits')
  if (config.provider === 'local') parts.push(hostOf(config.baseUrl?.trim() || DEFAULT_LOCAL_BASE_URL))
  return parts.join(' · ')
}
